import { ExpandIcon } from "@gettactic/components";
import { cn, useDisclosure } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { Button } from "@v2/components/ui/button";
import { MapFiltersCard } from "@webV2/components/cards/MapFiltersCard/MapFiltersCard";
import { MapReserveCard } from "@webV2/components/cards/MapReserveCard/MapReserveCartd";
import { MapUserCard } from "@webV2/components/cards/MapUserCard/MapUserCard";
import { useMapModalUrlSync } from "@webV2/hooks/useMapModalUrlSync";
import { useMap } from "@webV2/map/MapProvider";
import { MapView } from "@webV2/map/MapView/MapView";
import { useMapViewSvgEdit } from "@webV2/map/MapView/MapViewSvg/useMapViewSvgEdit";
import { useMapPageUrlSync } from "@webV2/pages/MapPage/useMapPageUrlSync";
import { addMinutes, format } from "date-fns";
import { X } from "lucide-react";
import React from "react";

const classes = {
  Wrapper: "map-page h-full relative",
  FiltersSidebar:
    "filters-sidebar md:ml-1 md:[.tactic-v2_&]:ml-0 md:p-v2-layout-card-out md:w-[440px] md:h-full md:relative",
  MapWrapper: "absolute inset-0 overflow-hidden",
  NavigationButtons:
    "h-8 w-8 rounded-full bg-background/90 backdrop-blur-sm hover:bg-background text-neutral-500",
  NavigationButtonsIcon: "h-4 w-4"
};

import { useReserve } from "@webV2/providers/ReserveProvider/context";
import { ReserveModalDisclosureOptions } from "@webV2/providers/ReserveProvider/types";

type MapPageContentProps = {
  isModal?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
  isOnReservationModal?: boolean;
  duplicateData?: ReserveModalDisclosureOptions;
};

export const MapPageContent: React.FC<MapPageContentProps> = ({
  isModal,
  onSuccess,
  onClose,
  isOnReservationModal,
  duplicateData
}) => {
  const dayViewDisclosure = useDisclosure({ defaultIsOpen: false });
  const {
    fullscreenMapDisclosure,
    startDate,
    endDate,
    floor,
    timeMode,
    defaultTab: section,
    filterQuery,
    mapInstanceId,
    changeStartDate,
    changeEndTime
  } = useMap();
  const editor = useMapViewSvgEdit();
  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();

  const { openReserveModal, closeReserveModal, isReserveOpen } = useReserve();

  const handleModalClose = () => {
    if (isReserveOpen) {
      // User clicked X button - reset to initial time
      const initialTime = closeReserveModal();
      if (initialTime) {
        changeStartDate(initialTime.start || null);
        changeEndTime(addMinutes(initialTime.start || new Date(), initialTime.duration || 0) || null);
      }
      setTimeout(() => {
        onClose?.();
      }, 300);
    } else {
      onClose?.();
    }
  };

  const handleExternalLink = () => {
    const params = new URLSearchParams();

    if (currentOffice?.id) params.set("officeId", currentOffice.id);
    if (floor?.id) params.set("floorId", floor.id);

    if (startDate) {
      const dateStr = `${format(startDate, "yyyy-MM-dd")}T${format(startDate, "HH:mm:ss")}`;
      params.set("date", dateStr);
      params.set("startTime", dateStr);
    }

    if (endDate) {
      const dateStr = `${format(endDate, "yyyy-MM-dd")}T${format(endDate, "HH:mm:ss")}`;
      params.set("endTime", dateStr);
    }

    if (timeMode) params.set("timeMode", timeMode);
    if (section) params.set("section", section);
    if (filterQuery) params.set("searchText", filterQuery);

    // Construct /map URL with all current params
    const url = `/map?${params.toString()}`;
    window.open(url, "_blank");
  };

  React.useEffect(() => {
    if (duplicateData) {
      // Force save initial time for duplicate data
      openReserveModal(duplicateData, false, true);
    }
  }, [duplicateData]);

  // Use different URL sync hooks based on context
  // Call hooks unconditionally, but they'll handle their own logic
  useMapPageUrlSync(!isModal);
  
  // We enable time sync even in reservation modal to show correct URL
  useMapModalUrlSync(isModal, false);

  const isMapEditing =
    editor?.isResourcePanelEditorOpen || editor?.isMapBuilderEditorOpen;

  return (
    <div className={classes.Wrapper}>
      {isModal && (
        <div className="absolute top-4 right-4 z-50 flex">
          <Button
            variant="ghost"
            size="icon"
            className={classes.NavigationButtons}
            onClick={handleExternalLink}
          >
            <ExpandIcon className={classes.NavigationButtonsIcon} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className={classes.NavigationButtons}
            onClick={handleModalClose}
          >
            <X className={classes.NavigationButtonsIcon} />
          </Button>
        </div>
      )}
      <div className={classes.MapWrapper}>
        <MapView
          isOnReservationModal={isOnReservationModal}
          embedded={false}
          editor={editor}
          isModal={isModal}
        />
      </div>
      {!fullscreenMapDisclosure.isOpen &&
        !editor.isResourcePanelEditorOpen &&
        !editor?.isMapBuilderEditorOpen && (
          <div
            className={cn(
              `${classes.FiltersSidebar} ${isModal ? " [.tactic-v2_&]:md:m-0 [.tactic-v2_&]:md:rounded-none [.tactic-v2_&]:md:p-0" : ""}`
            )}
          >
            <div id={`map-daily-view-card-holder-${mapInstanceId.replace(/:/g, "")}`} />
            {!isMapEditing && (
              <MapFiltersCard
                isModal={isModal}
                dayViewDisclosure={dayViewDisclosure}
              />
            )}
            <MapUserCard />
            <MapReserveCard
              isModal={isModal}
              isOnReservationModal={isOnReservationModal}
              dayViewDisclosure={dayViewDisclosure}
              onSuccess={onSuccess}
              onClose={onClose}
            />
          </div>
        )}
    </div>
  );
};
